import ApiServiceBase from "@/services/api/apiServiceBase";
import {EventFilterIs} from "@/definitions/EventFilterIs";
import {dataUrlToBlob, normalizePaginated} from "@/services/api/normalizeProfile";
import {normalizeEvent, normalizeEvents} from "@/services/api/normalizeFeed";

function asPaginatedLegacy(payload: any) {
    const {data, pagination} = normalizePaginated(payload);
    const events = normalizeEvents(data);
    return {
        data: events,
        meta: {
            current_page: pagination.page ?? 1,
            last_page: pagination.totalPages ?? 1,
            per_page: pagination.limit ?? 20,
            total: pagination.total ?? events.length,
        },
        pagination,
    };
}

export class EventsService extends ApiServiceBase {

    public async all(_is?: EventFilterIs): Promise<any> {
        const payload = await this.http?.get('/events', {page: 1, limit: 50});
        return normalizeEvents(normalizePaginated(payload).data);
    }

    public async paginate(page = 1, is?: EventFilterIs, extra: Record<string, any> = {}): Promise<any> {
        const params: Record<string, any> = {page, limit: 20, ...extra};
        if (is) {
            // Map legacy EventFilterIs onto contract query where possible
            params.is = is;
        }
        return asPaginatedLegacy(await this.http?.get('/events', params));
    }

    public async my(page = 1, limit = 10): Promise<any> {
        return asPaginatedLegacy(await this.http?.get('/events/my', {page, limit}));
    }

    public async attendingMe(page = 1, limit = 20): Promise<any> {
        return asPaginatedLegacy(await this.http?.get('/events/attending/me', {page, limit}));
    }

    public async get(eventId?: number): Promise<any> {
        return normalizeEvent(await this.http?.get(`/events/${eventId}`));
    }

    public async status(eventId: number): Promise<any> {
        return this.http?.get(`/events/${eventId}/status`);
    }

    public async comments(eventId?: number): Promise<any> {
        // Comments are embedded on event detail in the new API
        const event = await this.get(eventId);
        return event?.comments ?? event?.data?.comments ?? [];
    }

    public async comment(comment: string, eventId: number, commentParentId?: number): Promise<any> {
        const form = new FormData();
        form.append('eventId', String(eventId));
        form.append('comment', comment);
        if (commentParentId != null) {
            form.append('parentId', String(commentParentId));
        }
        return this.http?.upload('/events/comment', form);
    }

    public async deleteComment(commentId: number): Promise<any> {
        return this.http?.delete(`/events/comment/${commentId}`, {});
    }

    public async interested(eventId: number, _share?: string | null): Promise<any> {
        return this.http?.post('/events/interested', {eventId: Number(eventId)});
    }

    public async notInterested(eventId: number): Promise<any> {
        return this.http?.post('/events/not-interested', {eventId: Number(eventId)});
    }

    public async attending(eventId: number, _share?: string | null): Promise<any> {
        return this.http?.post('/events/attending', {eventId: Number(eventId)});
    }

    public async notAttending(eventId: number): Promise<any> {
        return this.http?.post('/events/not-attending', {eventId: Number(eventId)});
    }

    public async requestInvite(eventId: number): Promise<any> {
        return this.http?.post(`/events/${eventId}/request-invite`, {});
    }

    public async cancelRequestInvite(eventId: number): Promise<any> {
        return this.http?.delete(`/events/${eventId}/request-invite`, {});
    }

    public async confirmInvite(eventId: number, userId: number): Promise<any> {
        return this.http?.post(`/events/${eventId}/user/${userId}/confirm`, {});
    }

    public async rejectInvite(eventId: number, userId: number, _reason = ''): Promise<any> {
        return this.http?.post(`/events/${eventId}/user/${userId}/reject`, {});
    }

    public async deleteAttendanceRecord(eventId: number, userId: number): Promise<any> {
        return this.http?.delete(`/events/${eventId}/user/${userId}`, {});
    }

    public async delete(eventId: number): Promise<any> {
        return this.http?.delete(`/events/${eventId}`, {});
    }

    /** Removed from contract — return zero pending so UI does not crash. */
    public async pendingResponse(): Promise<{pending: number}> {
        return {pending: 0};
    }

    public async create(form: any): Promise<any> {
        const body = await this.toEventFormData(form);
        return this.http?.upload('/events', body);
    }

    public async update(form: any): Promise<any> {
        const eventId = form.id ?? form.eventId;
        const body = await this.toEventFormData(form, true);
        return this.http?.upload(`/events/${eventId}`, body, undefined, 'PUT');
    }

    public async imageFor(eventId: number, image: string): Promise<any> {
        const form = new FormData();
        const blob = await dataUrlToBlob(image);
        form.append('photo', blob, 'cover.jpg');
        return this.http?.upload(`/events/${eventId}`, form, undefined, 'PUT');
    }

    public async deleteImageFor(eventId: number): Promise<any> {
        const form = new FormData();
        form.append('removePhotoPath', 'true');
        return this.http?.upload(`/events/${eventId}`, form, undefined, 'PUT');
    }

    public async reveal(eventId: number): Promise<any> {
        return this.http?.post('/events/reveal', {eventId: Number(eventId)});
    }

    public async reactComment(commentId: number): Promise<any> {
        return this.http?.post('/events/react', {commentId: Number(commentId)});
    }

    public async unreactComment(reactionId: number): Promise<any> {
        return this.http?.delete(`/events/react/${reactionId}`, {});
    }

    private async toEventFormData(form: any, isUpdate = false): Promise<FormData> {
        const body = new FormData();
        const map: Record<string, string> = {
            name: 'name',
            type: 'type',
            visibility: 'visibility',
            startsAt: 'startsAt',
            starts_at: 'startsAt',
            description: 'description',
            rules: 'rules',
            location: 'location',
            lat: 'lat',
            lng: 'lng',
            capacity: 'capacity',
            endsAt: 'endsAt',
            ends_at: 'endsAt',
            allowComments: 'allowComments',
            allow_comments: 'allowComments',
            published: 'published',
            publicGuestList: 'publicGuestList',
            public_guest_list: 'publicGuestList',
            isNsfw: 'isNsfw',
            removePhotoPath: 'removePhotoPath',
        };

        for (const [src, dest] of Object.entries(map)) {
            if (form[src] != null && form[src] !== '') {
                body.append(dest, String(form[src]));
            }
        }

        if (!isUpdate) {
            if (!form.name) body.append('name', '');
            if (!form.type) body.append('type', 'HOUSE_PARTY');
            if (!form.visibility) body.append('visibility', 'public');
            if (!form.startsAt && !form.starts_at) {
                body.append('startsAt', new Date().toISOString());
            }
        }

        const photo = form.photo ?? form.image ?? form.cover;
        if (photo) {
            if (typeof photo === 'string') {
                const blob = await dataUrlToBlob(photo);
                body.append('photo', blob, 'cover.jpg');
            } else {
                body.append('photo', photo, (photo as File).name || 'cover.jpg');
            }
        }

        return body;
    }
}
